import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { dispositionFilename } from './campaign-pdf';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, Subscription, retry, catchError, combineLatest, defer, exhaustMap, expand, finalize, map, of, shareReplay, switchMap, takeWhile, tap, throwError, timeout, timer } from 'rxjs';
import { FORCE_REGION } from '../../core/http/http-context.tokens';
import { getRegion } from '../../core/http/env-utils';

export interface NudgeLibraryEntry {
  target: string;
  incentiveID: string;
  screenshot: string;
  html: string;
  devices: string[];
  title: string;
  copy: string;
  description: string;
  effect: string;
  pattern: string;
  placement: string;
  incentiveType: string;
  sector: string;
  primaryKpi: string;
}

export interface NudgePortRequest {
  target: string;
  /**
   * The campaign this run belongs to — required by `/port/run` since
   * FRONTEND_PORT_CAMPAIGNS.md. Every result, placement token and preview link
   * the run produces carries it, and the runtime only serves nudges matching
   * the token's campaign.
   *
   * Typed optional so the older Explore entry point still compiles; the Nudge
   * Library flow will not submit without one.
   */
  campaignId?: string;
  /**
   * The language every nudge in this run is written in (uppercase 2-letter).
   *
   * Chosen once, up front: it steers the AI writing the copy, is stored on
   * each ported nudge, and is reused as the incentive's language when the
   * nudge is finished — so language is never asked for again downstream.
   * One language per run; two languages means two runs.
   */
  language?: string;
  /**
   * The client's objectives (what the nudges should achieve, what to avoid),
   * steering the copy the AI writes for every nudge in the run
   * (site-suggestions-objectives-backend-handoff.md).
   */
  context?: string;
  /**
   * The store's design brief (notes and files on how the nudges should look)
   * the adaptation designs the nudges after; absent when the step was
   * skipped (nudge-design-brief-backend-handoff.md).
   */
  designBriefId?: string;
  nudges: NudgeLibraryEntry[];
  pageUrls?: Record<string, string>;
  sourceTarget?: string;
}

/**
 * A campaign's visitor experiment — its isolation settings and lifecycle.
 *
 * Only present when the target is opted into campaign isolation. `shared` is
 * an ordinary grouping that runs alongside everything else; `alone` reserves
 * `trafficPercent` of the target's visitors for this campaign and splits them
 * `testPercent` / rest, with neither arm receiving any other builder campaign.
 *
 * `status`, `activatedAt` and `allocationVersion` are the backend's, never
 * sent on create.
 */
export interface CampaignExperiment {
  isolationMode: 'shared' | 'alone';
  /** share of the target's visitors reserved for this campaign */
  trafficPercent: number;
  /** share of the reserved visitors that may receive nudges; the rest control */
  testPercent: number;
  status?: CampaignExperimentStatus;
  activatedAt?: string;
  startDate?: string | null;
  endDate?: string | null;
  allocationVersion?: string;
}

export type CampaignExperimentStatus = 'draft' | 'live' | 'paused' | 'ended';

/** `GET /port/campaigns/options` — whether this target may isolate at all. */
export interface CampaignExperimentOptions {
  enabled: boolean;
}

/** One arm's raw counts. */
export interface CampaignOverview {
  measurement: 'unique-campaign-participation';
  total: { visits: number; orders: number };
  groups: { test: { visits: number; orders: number }; control: { visits: number; orders: number } };
  incompleteData: boolean;
}

export interface CampaignReportGroup {
  visits: number;
  orders: number;
  revenue: number;
  addToCarts?: number;
  atcOpportunities?: number;
}

/** A ratio per arm, with the test arm's relative uplift over control. */
export interface CampaignReportMetric {
  significance?: { pc: number; method?: string } | null;
  significanceReason?: string | null;
  test: number | null;
  control: number | null;
  /** relative, in % — null when either side has no denominator */
  uplift: number | null;
}

/**
 * `GET /port/campaigns/report` — the campaign's own participation and order
 * facts, deduplicated per visit and per order.
 *
 * These are NOT the nudge rows added up: campaigns overlap, a visit counts
 * once here, and summing per-nudge reports would double-count it. Every ratio
 * is null rather than zero when its denominator is missing.
 */
export interface CampaignReport {
  nudgeId?: string | null;
  measurement?: string;
  series?: Array<{ time: string; metrics: CampaignReport['metrics']; groups: CampaignReport['groups'] }>;
  seriesMode?: string;
  groups: { test: CampaignReportGroup; control: CampaignReportGroup };
  metrics: {
    cr: CampaignReportMetric;
    rpv: CampaignReportMetric;
    aov: CampaignReportMetric;
    atcr?: CampaignReportMetric;
    /** revenue the test arm is estimated to have added, in the shop's currency */
    incrementalRevenue: number | null;
  };
  /** when participation was first recorded — nothing before it is measured */
  trackingStartedAt?: string | null;
  /** the window is missing facts (interrupted projection, or still catching up) */
  incompleteData?: boolean;
  /** why the figures are partial or absent, in the backend's words */
  reason?: string | null;
}

/** A target-scoped grouping for ported nudges (`portCampaigns`). */
export interface CampaignConditionDefaults {
  audience: {enabled: boolean; root: {type: string; match: string; children: any[]}};
  trigger: {enabled: boolean; match: string; conditions: any[]};
  deliveryLimits: {enabled: boolean; maxPerSession: number; maxPerPage: number; cooldownSeconds: number; delaySeconds: number; type: string};
  globalControlPercentage: number;
}
export interface NudgePortCampaign {
  signalsAudience?: {name:string;stage:string;segment:string|null;focus:string;threshold:number;description:string;revision:string};
  signalsAction?: 'nudge' | 'incentive';
  conditionDefaults?: CampaignConditionDefaults;
  campaignId: string;
  target: string;
  name: string;
  description?: string;
  /** the campaign's own figures — the only counts the list read carries */
  nudgeCount?: number;
  adaptationCount?: number;
  /**
   * Only the nudges that have already produced an adaptation, so a campaign
   * mid-run reports `[]`. Use `nudges` for what is in the campaign — see
   * `portCampaignNudgeIds` below.
   */
  nudgeIds?: string[];
  /**
   * Per-nudge adaptation state — the same shape a running job reports, but
   * here it is the campaign's own record, so it survives the job that produced
   * it. This is what the Detailed table's status column reads.
   */
  nudges?: NudgePortNudgeStatus[];
  /**
   * One tokenized link per page type that has ready nudges — only returned
   * when a single campaign is requested by id. Tokens live ~1h and are reused
   * while valid, so fetch these when the user is about to open one.
   */
  previewLinks?: NudgePortCampaignPreviewLink[];
  /** absent on a legacy campaign, and on targets without isolation */
  experiment?: CampaignExperiment | null;
  /**
   * The signed-in user's "Email me when pages are ready" setting — only on
   * the single-campaign read (campaign-ready-email-backend-handoff.md). Absent
   * while the backend does not support it, and the setup then hides the
   * switch rather than offer one that sends nothing.
   */
  readyNotification?: CampaignReadyNotification | null;
  /** null on older campaigns whose creation date was never stored */
  createdAt?: string | null;
  /** who created it; `name` may be null when only an email is known */
  createdBy?: CampaignCreator | null;
  updatedAt?: string;
}

export interface CampaignCreator {
  email?: string | null;
  name?: string | null;
}

/** "Created by …": the creator's name, else their email, else ''. */
export function campaignCreatorLabel(campaign?: { createdBy?: CampaignCreator | null } | null): string {
  const creator = campaign?.createdBy;
  return creator?.name?.trim() || creator?.email?.trim() || '';
}

/**
 * Another nudge in the same campaign built from the same source template
 * (`campaignMatches`). Informational only: it blocks nothing.
 */
export interface CampaignNudgeMatch {
  nudgeId: string;
  /** the generation batch that made it — not an experiment id */
  jobId?: string;
  name?: string | null;
  status?: string;
}

/** The raw-data export's device filter; `overall` is every device. */
export type CampaignRawDataDevice = 'overall' | 'desktop' | 'mobile' | 'tablet';

/** The raw-data export's range limit; longer ranges are refused. */
export const CAMPAIGN_RAW_DATA_MAX_DAYS = 31;

/** What a campaign PDF covers; see `downloadCampaignPdf`. */
export type CampaignPdfOptions =
  | { kind?: 'review'; includeFaq?: boolean }
  | { kind: 'results'; startDate?: string; endDate?: string };

/** Whether the signed-in user is emailed when this campaign's nudges are prepared. */
export interface CampaignReadyNotification {
  enabled: boolean;
}

export interface NudgePortCampaignPreviewLink {
  pageType: string;
  siteID: number;
  url: string;
  token: string;
  tokenParam: string;
  previewUrl: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

interface AcceptedJob {
  startedBy?: { userId?: string | null; name?: string | null; email?: string | null } | null;
  jobId: string;
  target: string;
  total: number;
  nudges: number;
  /** the port campaign the run belongs to; absent on legacy jobs */
  campaignId?: string;
  campaignName?: string;
}

export interface NudgePortError {
  sourceIncentiveID: string;
  device: string;
  status: string;
  error: string;
}

/**
 * Per-nudge progress inside a run (`nudgeStatuses[]` on the job detail).
 *
 * The expected set is fixed when the job starts, so everything appears up
 * front as `pending` and flips as it finishes rather than popping in.
 */
export interface NudgePortNudgeStatus {
  name?: string | null;
  ruleStatus?: string | null;
  displayStatus?: string | null;
  sourceTemplateId?: string;
  sourceIncentiveID: string;
  pageType: string;
  /** per viewport: pending | ready | error */
  devices?: Record<string, string>;
  /** the adapted nudge's id per viewport, once it exists */
  ids?: Record<string, string>;
  /** rolled up across viewports */
  status: 'pending' | 'partial' | 'ready' | 'error';
  /** every viewport is ready */
  ready?: boolean;
  /** at least one viewport is ready — enough to open it in the picker */
  testable?: boolean;
  /** Finish passed for the current saved design. Generation readiness alone is insufficient. */
  reviewComplete?: boolean;
  /** This generated nudge has passed Finish at least once, even if later edited or offline. */
  finishedOnce?: boolean;
  /** The campaign already has a nudge from the same source template. */
  alreadyInCampaign?: boolean;
  campaignMatches?: CampaignNudgeMatch[];
}

/** Per-nudge state — carried by the single-campaign read, not by the list. */
export function portCampaignNudges(
  campaign?: NudgePortCampaign | null,
): NudgePortNudgeStatus[] {
  return campaign?.nudges || [];
}

/** What the dashboard shows for an adapted nudge. */
export type NudgePortNudgeState = 'ready' | 'pending' | 'error';

/**
 * The backend rolls a nudge up into four states; the screens show three.
 *
 * `partial` — one viewport adapted, the other not — is not a state anyone can
 * act on: the second is still on its way, so it reads as `pending` like any
 * other unfinished nudge. A viewport that failed is the exception, because
 * that one is not coming.
 *
 * The stat cards, their row filter and the table's status chip all derive
 * from this, so a row labelled Ready is always one the Completed card counts.
 */
export function portNudgeState(
  nudge: NudgePortNudgeStatus,
): NudgePortNudgeState {
  if (nudge.status === 'error') return 'error';
  const devices = nudge.devices || {};
  const names = Object.keys(devices);
  // no per-viewport breakdown: the roll-up is all there is
  if (!names.length) {
    return nudge.ready === true || nudge.status === 'ready'
      ? 'ready'
      : 'pending';
  }
  if (names.every((device) => devices[device] === 'ready')) return 'ready';
  if (names.some((device) => devices[device] === 'error')) return 'error';
  return 'pending';
}

/**
 * A campaign's lifecycle as the UI shows it: an experiment past its end date
 * reads as ended even before the server flips its status.
 */
export function portCampaignExperimentStatus(campaign: NudgePortCampaign): CampaignExperimentStatus {
  const experiment = campaign.experiment;
  if (experiment?.endDate && Date.parse(experiment.endDate) <= Date.now() && experiment.status !== 'draft') return 'ended';
  return experiment?.status || 'draft';
}

/** The status chip's text: Active / Scheduled / Paused / Ended / Draft. */
export function portCampaignStatusLabel(campaign: NudgePortCampaign): string {
  if (campaign.experiment?.status === 'live' && campaign.experiment.startDate && Date.parse(campaign.experiment.startDate) > Date.now()) return 'Scheduled';
  switch (portCampaignExperimentStatus(campaign)) {
    case 'live':
      return 'Active';
    case 'paused':
      return 'Paused';
    case 'ended':
      return 'Ended';
    default:
      return 'Draft';
  }
}

/**
 * The campaign's headline figures.
 *
 * `nudgeCount`/`adaptationCount` are the endpoint's own, and the only ones the
 * LIST read carries — it returns no `nudges`, so counting that array there
 * gives zero for every campaign. `nudges` is the fallback, for a response that
 * omits the counts.
 */
export function portCampaignNudgeCount(
  campaign?: NudgePortCampaign | null,
): number {
  return typeof campaign?.nudgeCount === 'number'
    ? campaign.nudgeCount
    : portCampaignNudges(campaign).length;
}

/** Device adaptations written — one per viewport, so up to 2 per nudge. */
export function portCampaignAdaptationCount(
  campaign?: NudgePortCampaign | null,
): number {
  return typeof campaign?.adaptationCount === 'number'
    ? campaign.adaptationCount
    : portCampaignNudges(campaign).reduce(
        (sum, nudge) => sum + Object.keys(nudge?.ids || {}).length,
        0,
      );
}

/**
 * The campaign's source nudge ids, which scope the Detailed table.
 *
 * Read off `nudges` rather than `nudgeIds`, which lists only the nudges that
 * already adapted — a campaign mid-run would scope the table to nothing.
 */
export function portCampaignNudgeIds(
  campaign?: NudgePortCampaign | null,
): string[] {
  const ids = new Set<string>();
  for (const nudge of portCampaignNudges(campaign)) {
    const id = String(nudge?.sourceIncentiveID || '');
    if (id) ids.add(id);
  }
  return [...ids];
}

interface JobStatus extends AcceptedJob {
  status: 'running' | 'canceling' | 'completed' | 'error' | 'canceled';
  done: number;
  ready: number;
  needsReview: number;
  failed: number;
  errors: NudgePortError[];
  error?: string | null;
  createdAt?: string;
  updatedAt?: string;
  finishedAt?: string | null;
  /** only on the job DETAIL read, never the list */
  nudgeStatuses?: NudgePortNudgeStatus[];
  /** minted once the run is completed/canceled */
  previewLinks?: NudgePortCampaignPreviewLink[];
}

type JobSummary = Omit<JobStatus, 'errors' | 'needsReview'> & Partial<Pick<JobStatus, 'errors' | 'needsReview'>>;

interface JobList {
  count: number;
  active: number;
  jobs: JobSummary[];
}

export interface NudgeJobsView {
  jobs: NudgePortJob[];
  active: number;
  loading: boolean;
  error: string;
}

/**
 * A job this session knows about. Nothing here is persisted — the endpoint is
 * the only record of what is running (see `refreshActive`).
 */
interface TrackedJob extends AcceptedJob {
  targetName: string;
  region: string;
}

export interface NudgePortJob extends TrackedJob, JobStatus {
  key: string;
  monitorError: string;
  pollingPaused: boolean;
  cancelState?: 'requesting' | 'canceling';
  cancelError?: string;
}

/**
 * The trimmed link `getJobResult` hands back. `siteID` is numeric, matching
 * both the campaign links and the first segment of a nudge id.
 */
export interface NudgeJobPreviewLink {
  siteID: number;
  pageType: string;
  previewUrl: string;
}

export interface NudgeJobResult {
  job: NudgePortJob;
  previewLinks: NudgeJobPreviewLink[];
}

/** Owns job monitoring so switching tabs does not cancel a submitted adaptation. */
@Injectable({ providedIn: 'root' })
export class NudgeLibraryPortService implements OnDestroy {
  private readonly state = new BehaviorSubject<NudgePortJob[]>([]);
  readonly jobs$ = this.state.asObservable();
  private readonly polls = new Map<string, Subscription>();
  private readonly listings = new Set<Subscription>();
  private readonly cancellations = new Set<Subscription>();
  private readonly discovering = new Set<string>();
  private destroyed = false;

  constructor(private readonly http: HttpClient, private readonly zone: NgZone) {}

  /* --------------------------- campaigns ------------------------- */

  private retryTransientRead<T>() {
    return retry<T>({count:2,delay:(error,index)=> error.name==='TimeoutError' || error.status===0 || error.status===429 || error.status>=500 ? timer(500*index) : throwError(()=>error)});
  }

  /** `GET /nudge-builder/port/campaigns?target=` */
  listCampaigns(target: string, region = getRegion()): Observable<NudgePortCampaign[]> {
    if (!target) return of([]);
    return this.http.get<ApiResponse<{ count: number; campaigns: NudgePortCampaign[] }>>(
      '/nudge-builder/port/campaigns', { params: { target }, context: this.context(region) },
    ).pipe(
      timeout(30000),
      this.retryTransientRead(),
      map(response => {
        const data = this.unwrap(response);
        if(!Array.isArray(data?.campaigns)) throw new Error('Campaign list response is unavailable.');
        return data.campaigns;
      }),
    );
  }

  /**
   * `GET /nudge-builder/port/campaigns?target=&campaignId=` — the single-campaign
   * read, which is the only one that mints `previewLinks`.
   */
  getCampaign(target: string, campaignId: string, region = getRegion()): Observable<NudgePortCampaign | null> {
    if (!target || !campaignId) return of(null);
    return this.http.get<ApiResponse<{ count: number; campaigns: NudgePortCampaign[] }>>(
      '/nudge-builder/port/campaigns', { params: { target, campaignId }, context: this.context(region) },
    ).pipe(
      timeout(30000),
      map(response => {
        const data = this.unwrap(response);
        const found = Array.isArray(data?.campaigns) ? data.campaigns : [];
        return found.find(c => c.campaignId === campaignId) || found[0] || null;
      }),
    );
  }

  /* ------------------- campaign isolation + report ----------------- */

  /**
   * `GET /port/campaigns/options` — is this target opted into isolation?
   *
   * Off by default and per target, so the create dialog asks before offering
   * any of it; a target that answers `false` (or errors) keeps creating plain
   * groupings.
   */
  experimentOptions(
    target: string,
    region = getRegion(),
  ): Observable<CampaignExperimentOptions> {
    if (!target) return of({ enabled: false });
    return this.http.get<ApiResponse<CampaignExperimentOptions>>(
      '/nudge-builder/port/campaigns/options',
      { params: { target }, context: this.context(region) },
    ).pipe(
      timeout(30000),
      map(response => ({ enabled: this.unwrap(response)?.enabled === true })),
    );
  }

  /**
   * `PUT /port/campaigns/experiment` — the campaign's lifecycle.
   *
   * `status: 'live'` activates it, which locks its isolation settings, its
   * traffic and test/control split. New nudges can be added later. `'paused'`
   * suppresses its rules but keeps the visitors reserved; `'ended'` is
   * permanent and releases them for new visits.
   */
  saveExperiment(
    target: string,
    campaignId: string,
    settings: Partial<CampaignExperiment>,
    region = getRegion(),
  ): Observable<NudgePortCampaign | null> {
    if (!target || !campaignId) {
      return throwError(() => new Error('Choose a campaign first.'));
    }
    return this.http.put<ApiResponse<{ campaign?: NudgePortCampaign }>>(
      '/nudge-builder/port/campaigns/experiment',
      { target, campaignId, ...settings },
      { context: this.context(region) },
    ).pipe(
      timeout(30000),
      map(response => {
        const data = this.unwrap(response);
        return ((data?.campaign ?? data) as NudgePortCampaign) || null;
      }),
    );
  }

  /**
   * `PUT /port/campaigns/notifications` — email the signed-in user when this
   * campaign's adaptation runs finish ("Email me when pages are ready").
   * Answers with the setting as saved.
   */
  saveReadyNotification(
    target: string,
    campaignId: string,
    enabled: boolean,
    region = getRegion(),
  ): Observable<CampaignReadyNotification> {
    if (!target || !campaignId) {
      return throwError(() => new Error('Choose a campaign first.'));
    }
    return this.http.put<ApiResponse<{ readyNotification?: CampaignReadyNotification }>>(
      '/nudge-builder/port/campaigns/notifications',
      { target, campaignId, readyEmail: enabled },
      { context: this.context(region) },
    ).pipe(
      timeout(30000),
      map(response => {
        const saved = this.unwrap(response)?.readyNotification;
        return { enabled: typeof saved?.enabled === 'boolean' ? saved.enabled : enabled };
      }),
    );
  }

  /**
   * `POST /port/campaigns/pdf` — a campaign PDF, generated and returned in
   * this one request (no job, no polling). Two kinds:
   * - the summary / review (the default: `{ includeFaq: true }`, no `kind`) —
   *   the campaign's rules, nudge previews and FAQ, for review before launch;
   * - live results (`{ kind: 'results', startDate?, endDate? }`) — the
   *   campaign's reporting figures, for a live campaign only (409 otherwise).
   *   Without dates it covers activation until now; the end is exclusive and
   *   may not lie in the future.
   *
   * 200 is the binary PDF, named in `Content-Disposition` (readable only when
   * the backend exposes that header to CORS). Errors are `{ success: false,
   * message }` JSON — arriving as a Blob too, since the response type is.
   * Generation can take minutes, hence the long timeout; never retried, so
   * one click is one request.
   */
  /**
   * `GET /port/campaigns/:campaignId/raw-data`: the campaign's records for a
   * reporting range, as a CSV — the file itself, in one request (no job, no
   * second download). `endDate` is exclusive; at most 31 days, 100,000
   * records and 25 MiB. Errors are `{ message }` JSON arriving as a Blob:
   * 413 too large, 504 timed out — both mean a shorter range.
   */
  downloadCampaignRawData(
    target: string,
    campaignId: string,
    range: { startDate: string; endDate: string; device?: CampaignRawDataDevice },
    region = getRegion(),
  ): Observable<{ blob: Blob; filename: string | null }> {
    if (!target || !campaignId) {
      return throwError(() => new Error('Choose a campaign first.'));
    }
    // the id is in the path and, as the backend asks, in the query too
    const params: Record<string, string> = {
      target,
      campaignId,
      startDate: range.startDate,
      endDate: range.endDate,
    };
    if (range.device) params['device'] = range.device;
    return this.http.get(`/nudge-builder/port/campaigns/${encodeURIComponent(campaignId)}/raw-data`, {
      params,
      context: this.context(region),
      observe: 'response',
      responseType: 'blob',
    }).pipe(
      timeout(60000),
      map(response => ({
        blob: response.body as Blob,
        filename: dispositionFilename(response.headers.get('Content-Disposition')),
      })),
    );
  }

  downloadCampaignPdf(
    target: string,
    campaignId: string,
    options: CampaignPdfOptions = { includeFaq: true },
    region = getRegion(),
  ): Observable<{ blob: Blob; filename: string | null }> {
    if (!target || !campaignId) {
      return throwError(() => new Error('Choose a campaign first.'));
    }
    return this.http.post('/nudge-builder/port/campaigns/pdf', { target, campaignId, ...options }, {
      context: this.context(region),
      observe: 'response',
      responseType: 'blob',
    }).pipe(
      timeout(240000),
      map(response => ({
        blob: response.body as Blob,
        filename: dispositionFilename(response.headers.get('Content-Disposition')),
      })),
    );
  }

  /**
   * `GET /port/campaigns/report` — the campaign's own results.
   *
   * Read from deduplicated participation and order facts for the window, at
   * the campaign's current allocation version. This is the only correct source
   * for a campaign total: per-nudge rows overlap, so adding them up or
   * averaging their uplifts counts the same visit twice.
   */
  campaignOverview(target: string, startDate: string, endDate: string): Observable<CampaignOverview> {
    return this.http.get<ApiResponse<CampaignOverview>>('/nudge-builder/port/campaigns/overview', {
      params: { target, startDate, endDate }, context: this.context(getRegion()),
    }).pipe(timeout(30000), map(response => this.unwrap(response) as CampaignOverview));
  }

  campaignNudgeReports(target: string, nudges: Array<{nudgeId:string;campaignId:string}>,
    startDate: string, endDate: string, device = '', chartDevice = 'overall', seriesRate = 'hourly', region = getRegion()
  ): Observable<{reports: CampaignReport[]; chartReports: CampaignReport[]}> {
    if (!nudges.length) return of({reports:[],chartReports:[]});
    return this.http.post<ApiResponse<{reports:CampaignReport[];chartReports:CampaignReport[]}>>(
      '/nudge-builder/port/campaigns/nudge-reports',
      {target,nudges,startDate,endDate,device:device || 'overall',chartDevice:chartDevice || 'overall',seriesRate},
      {context:this.context(region)},
    ).pipe(timeout(30000),this.retryTransientRead(),map(response=>{
      const data=this.unwrap(response);
      if (!Array.isArray(data?.reports) || !Array.isArray(data?.chartReports)) throw new Error('Nudge reports are unavailable.');
      return data;
    }));
  }

  campaignReport(
    target: string,
    campaignId: string,
    startDate: string,
    endDate: string,
    device = '',
    region = getRegion(),
    nudgeId = '',
    includeSeries = false,
    seriesRate = '',
  ): Observable<CampaignReport> {
    if (!target || !campaignId || !startDate || !endDate) {
      return throwError(() => new Error('The campaign report needs a period.'));
    }
    return this.http.get<ApiResponse<CampaignReport>>(
      '/nudge-builder/port/campaigns/report',
      {
        params: { target, campaignId, startDate, endDate, device, ...(nudgeId ? { nudgeId } : {}), ...(includeSeries ? { includeSeries: 'true', ...(seriesRate ? {seriesRate} : {}) } : {}) },
        context: this.context(region),
      },
    ).pipe(
      timeout(30000),
      this.retryTransientRead(),
      map(response => {
        const report = this.unwrap(response) as CampaignReport;
        if (nudgeId && (report?.nudgeId !== nudgeId || report?.measurement !== 'selected-eligible')) {
          throw new Error('Nudge-level campaign reporting is not available on this API.');
        }
        return report;
      }),
    );
  }

  /**
   * `POST /nudge-builder/port/campaigns` → 201.
   * Names are case-insensitively unique per target; a duplicate answers 409.
   */
  createCampaign(
    target: string,
    name: string,
    description = '',
    region = getRegion(),
    experiment?: CampaignExperiment,
    conditionDefaults?: CampaignConditionDefaults,
  ): Observable<NudgePortCampaign> {
    const trimmed = name.trim();
    if (!target || !trimmed) {
      return throwError(() => new Error('Give the campaign a name.'));
    }
    return this.http.post<ApiResponse<{ campaign?: NudgePortCampaign } & Partial<NudgePortCampaign>>>(
      '/nudge-builder/port/campaigns',
      {
        target,
        name: trimmed,
        // no `experiment` at all is a legacy grouping, which is what a target
        // without isolation must keep creating
        ...(experiment ? { experiment } : {}),
        ...(conditionDefaults ? { conditionDefaults } : {}),
        ...(description.trim() ? { description: description.trim() } : {}),
      },
      { params: { target }, context: this.context(region) },
    ).pipe(
      timeout(30000),
      map(response => {
        // the create route nests it: `{ success, data: { campaign: {...} } }`
        const data = this.unwrap(response);
        const campaign = (data?.campaign ?? data) as NudgePortCampaign;
        if (!campaign?.campaignId) throw new Error('The server did not return the new campaign.');
        return campaign;
      }),
    );
  }

  deleteFailedJob(job: NudgePortJob): Observable<any> {
    return this.http.delete<ApiResponse<any>>('/nudge-builder/port/job', {
      params:{target:job.target,jobId:job.jobId},context:this.context(job.region),
    }).pipe(map(response=>{
      const result=this.unwrap(response);
      this.stop(job.key);
      this.state.next(this.state.value.filter(item=>item.key!==job.key));
      return result;
    }));
  }

  retryNudge(job: NudgePortJob, sourceTemplateId: string): Observable<NudgePortJob> {
    return this.http.post<ApiResponse<AcceptedJob>>('/nudge-builder/port/retry',
      {target:job.target,jobId:job.jobId,sourceTemplateId},
      {params:{target:job.target},context:this.context(job.region)}).pipe(
        map(response=>{const accepted=this.unwrap(response);if(!accepted?.jobId || accepted.target!==job.target) throw new Error('Could not start retry');return this.track({...accepted,targetName:job.targetName,region:job.region});}),
        shareReplay({bufferSize:1,refCount:false}));
  }

  start(request: NudgePortRequest, targetName: string, region = getRegion()): Observable<NudgePortJob> {
    if (!request.target || !request.nudges?.length || request.nudges.length > 200) {
      return throwError(() => new Error('Choose a client and between 1 and 200 nudges.'));
    }
    return this.http.post<ApiResponse<AcceptedJob>>('/nudge-builder/port/run', request, {
      params: { target: request.target }, context: this.context(region),
    }).pipe(
      map(response => {
        const accepted = this.unwrap(response);
        if (!accepted?.jobId || accepted.target !== request.target) {
          throw new Error('The server did not return a valid job reference. Check the library before submitting again.');
        }
        return this.track({ ...accepted, targetName, region });
      }),
      // Keep the accepted job even if its originating component is destroyed during submission.
      // POST is deliberately never retried automatically: that could create duplicate jobs.
      shareReplay({ bufferSize: 1, refCount: false }),
    );
  }

  /**
   * One `/port/jobs?status=active` read: adopt whatever is running for this
   * client, whoever started it.
   *
   * Running jobs used to be mirrored into sessionStorage and re-adopted from
   * there after a reload. That made the page trust its own copy of state the
   * server owns — a job finished, canceled or removed elsewhere came back as
   * "running" until something corrected it. The endpoint is the only source
   * now, so a reload shows exactly what is actually running.
   *
   * Unlike `watchActiveJobs` this does not loop: it is for screens that only
   * need to know the jobs exist (the sidenav pill). Each job found is then
   * polled individually, which is what keeps the count moving.
   */
  refreshActive(target: string, targetName = ''): void {
    if (!target) return;
    const region = getRegion();
    const key = region + ':' + target;
    if (this.discovering.has(key)) return;
    this.discovering.add(key);
    let subscription: Subscription | undefined;
    subscription = this.http.get<ApiResponse<JobList>>('/nudge-builder/port/jobs', {
      params: { target, status: 'active', limit: '100' }, context: this.context(region),
    }).pipe(
      timeout(30000),
      finalize(() => { if (subscription) this.listings.delete(subscription); }),
    ).subscribe({
      next: response => {
        this.discovering.delete(key);
        const list = this.unwrap(response);
        if (!Array.isArray(list?.jobs)) return;
        this.mergeActiveJobs(list.jobs, target, targetName, region);
        // no listing loop behind this one, so the jobs it found need polling
        for (const job of this.state.value) {
          if (job.target === target && job.region === region && this.isActive(job.status)
            && !job.pollingPaused && !this.polls.has(job.key)) this.poll(job);
        }
      },
      // nothing is known rather than wrongly remembered; the next call retries
      error: () => this.discovering.delete(key),
    });
    if (!subscription.closed) this.listings.add(subscription);
  }

  /** Discover jobs from every session; the lightweight list supplies their live progress. */
  watchActiveJobs(target: string, targetName: string): Observable<NudgeJobsView> {
    if (!target) return of({ jobs: [], active: 0, loading: false, error: '' });
    const region = getRegion();
    // One listing request, re-scheduled after each result rather than on a fixed
    // interval — see `discoveryDelay`.
    const ask = defer(() => this.http.get<ApiResponse<JobList>>('/nudge-builder/port/jobs', {
      params: { target, status: 'active', limit: '100' }, context: this.context(region),
    }).pipe(
      timeout(30000),
      map(response => {
        const list = this.unwrap(response);
        if (!Array.isArray(list?.jobs)) throw new Error('Unrecognized jobs response.');
        this.zone.run(() => this.mergeActiveJobs(list.jobs, target, targetName, region));
        return { loading: false, error: '', paused: false };
      }),
      catchError(error => {
        const paused = error instanceof HttpErrorResponse && [401, 403, 404].includes(error.status);
        return of({ loading: false, paused, error: paused
          ? 'Unable to check active jobs. Check your access and try again.'
          : 'Unable to refresh active jobs. Retrying automatically; the last known progress is shown.' });
      }),
    ));
    const discovery = new Observable<{ loading: boolean; error: string }>(observer => {
      observer.next({ loading: true, error: '' });
      const subscription = this.zone.runOutsideAngular(() => ask.pipe(
        expand(result => result.paused
          ? EMPTY
          : timer(this.discoveryDelay(target, region)).pipe(switchMap(() => ask))),
        takeWhile(result => !result.paused, true),
      ).subscribe(result => this.zone.run(() => observer.next(result))));
      this.listings.add(subscription);
      return () => {
        subscription.unsubscribe();
        this.listings.delete(subscription);
        if (!this.destroyed) {
          // Continue monitoring known work when leaving this client or closing the library tab.
          for (const job of this.state.value) {
            if (job.target === target && job.region === region && this.isActive(job.status) && !job.pollingPaused && !this.polls.has(job.key)) {
              this.poll(job);
            }
          }
        }
      };
    });
    return combineLatest([this.jobs$, discovery]).pipe(map(([jobs, result]) => {
      const visible = jobs.filter(job => job.target === target && job.region === region);
      return { ...result, jobs: visible, active: visible.filter(job => this.isActive(job.status) && !job.pollingPaused).length };
    }));
  }

  /**
   * How long to wait before asking for the active list again.
   *
   * This listing exists to discover runs started in *another* session, so it
   * cannot stop just because we know of none — but it also has no reason to run
   * every 5 seconds when nothing is happening. It used to be a flat
   * `timer(0, 5000)`, which meant an idle Nudge Library tab hit
   * `/port/jobs` twelve times a minute forever. Fast while work is in flight,
   * lazy when idle.
   */
  private discoveryDelay(target: string, region: string): number {
    const busy = this.state.value.some(job =>
      job.target === target && job.region === region && this.isActive(job.status));
    return busy ? 5000 : 30000;
  }

  /** Fetch recent runs only when the jobs modal is opened or refreshed. */
  getRecentJobs(target: string, targetName: string): Observable<NudgePortJob[]> {
    if (!target) return of([]);
    const region = getRegion();
    return this.http.get<ApiResponse<JobList>>('/nudge-builder/port/jobs', {
      params: { target, limit: '50' }, context: this.context(region),
    }).pipe(
      timeout(30000),
      map(response => {
        const list = this.unwrap(response);
        if (!Array.isArray(list?.jobs)) throw new Error('Unrecognized jobs response.');
        for (const summary of list.jobs) {
          if (!summary.jobId || summary.target !== target || !this.validStatus(summary.status)) continue;
          const job = this.track({ ...summary, targetName, region }, summary);
          // Active polling may already have newer progress than this history snapshot.
          if (!this.isActive(summary.status)) {
            this.stop(job.key);
            this.update(this.mergeStatus(job, summary));
          }
        }
        return this.state.value.filter(job => job.target === target && job.region === region);
      }),
    );
  }

  /** Preview tokens are short-lived: return fresh links without storing them in job state. */
  getJobResult(job: NudgePortJob): Observable<NudgeJobResult> {
    return this.http.get<ApiResponse<JobStatus>>('/nudge-builder/port/job', {
      params: { jobId: job.jobId, target: job.target }, context: this.context(job.region),
    }).pipe(
      timeout(30000),
      map(response => {
        const data = this.unwrap(response);
        if (!data || !this.validStatus(data.status) || (data.jobId && data.jobId !== job.jobId) || (data.target && data.target !== job.target)) {
          throw new Error('Unrecognized job result.');
        }
        const current = this.state.value.find(item => item.key === job.key) || job;
        const updated = this.mergeStatus(current, data);
        if (updated.status !== 'running') this.stop(job.key);
        this.update(updated);
        const previewLinks = Array.isArray(data.previewLinks) ? data.previewLinks.filter(link => {
          if (!link || typeof link.previewUrl !== 'string' || typeof link.pageType !== 'string') return false;
          try {
            return ['https:', 'http:'].includes(new URL(link.previewUrl).protocol);
          } catch {
            return false;
          }
        }).map(({ siteID, pageType, previewUrl }) => ({ siteID: Number(siteID), pageType, previewUrl })) : [];
        return { job: updated, previewLinks };
      }),
    );
  }

  /** Cancellation is graceful: keep monitoring until the backend reports a terminal status. */
  cancel(job: NudgePortJob): void {
    const current = this.state.value.find(item => item.key === job.key);
    if (!current || current.status !== 'running' || current.cancelState) return;
    this.update({ ...current, cancelState: 'requesting', cancelError: '' });
    // Own the request here so closing the modal or leaving Explore cannot abort the stop request.
    const subscription = new Subscription();
    this.cancellations.add(subscription);
    subscription.add(this.http.post<ApiResponse<{ jobId: string; status: string }>>('/nudge-builder/port/job/cancel', {
      jobId: current.jobId,
    }, { params: { target: current.target }, context: this.context(current.region) }).pipe(
      timeout(30000),
      map(response => {
        const result = this.unwrap(response);
        if (result?.jobId !== current.jobId || result.status !== 'canceling') {
          throw new Error('The server did not confirm the stop request. Check the job status and try again.');
        }
      }),
      finalize(() => this.cancellations.delete(subscription)),
    ).subscribe({
      next: () => {
        const latest = this.state.value.find(item => item.key === current.key);
        if (!latest || latest.status !== 'running') return;
        const stopping: NudgePortJob = { ...latest, cancelState: 'canceling', cancelError: '' };
        this.update(stopping);
        if (stopping.pollingPaused || !this.polls.has(stopping.key)) this.retry(stopping);
      },
      error: error => {
        const latest = this.state.value.find(item => item.key === current.key);
        if (!latest || latest.status !== 'running') return;
        const conflict = error instanceof HttpErrorResponse && error.status === 409;
        const message = conflict
          ? 'This job has already finished. Refreshing its status…'
          : error instanceof HttpErrorResponse && error.status === 404
            ? 'This job could not be found for this client. Refresh the job list.'
            : error instanceof HttpErrorResponse && [401, 403].includes(error.status)
              ? 'Unable to stop this job. Check your access and try again.'
              : 'The stop request could not be confirmed. Check the job status and try again.';
        const updated = { ...latest, cancelState: undefined, cancelError: message };
        this.update(updated);
        if (conflict) this.retry(updated);
      },
    }));
  }

  retry(job: NudgePortJob): void {
    job = this.state.value.find(item => item.key === job.key) || job;
    if (job.status !== 'running') return;
    this.stop(job.key);
    const updated = { ...job, monitorError: '', pollingPaused: false };
    this.update(updated);
    this.poll(updated);
  }

  dismiss(job: NudgePortJob): void {
    if (job.status === 'running') return;
    this.stop(job.key);
    this.state.next(this.state.value.filter(item => item.key !== job.key));
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.listings.forEach(subscription => subscription.unsubscribe());
    this.listings.clear();
    this.cancellations.forEach(subscription => subscription.unsubscribe());
    this.cancellations.clear();
    this.polls.forEach(subscription => subscription.unsubscribe());
    this.polls.clear();
  }

  private track(tracked: TrackedJob, initial?: JobSummary): NudgePortJob {
    const key = JSON.stringify([tracked.region, tracked.target, tracked.jobId]);
    const existing = this.state.value.find(job => job.key === key);
    if (existing) return existing;
    const defaults: NudgePortJob = {
      ...tracked, key, status: 'running', done: 0, ready: 0, needsReview: 0, failed: 0,
      errors: [], monitorError: '', pollingPaused: false,
    };
    const job = initial ? this.mergeStatus(defaults, initial) : defaults;
    this.state.next([job, ...this.state.value]);
    if (!initial) this.poll(job);
    return job;
  }

  private mergeActiveJobs(list: JobSummary[], target: string, targetName: string, region: string): void {
    const activeKeys = new Set<string>();
    for (const summary of list) {
      if (!summary.jobId || summary.target !== target || !this.validStatus(summary.status)) continue;
      const job = this.track({ ...summary, targetName, region }, summary);
      activeKeys.add(job.key);
      // One list request replaces polling every active job individually.
      this.stop(job.key);
      this.update(this.mergeStatus(job, summary));
    }
    for (const job of this.state.value) {
      if (job.target === target && job.region === region && job.status === 'running' && !activeKeys.has(job.key) && !job.pollingPaused && !this.polls.has(job.key)) {
        // An absent job may have finished or fallen outside the limit; confirm its final status.
        this.poll(job);
      }
    }
  }

  private mergeStatus(job: NudgePortJob, data: JobSummary): NudgePortJob {
    if (job.status !== 'running' && data.status === 'running') return job;
    return {
      ...job, status: data.status,
      startedBy: data.startedBy ?? job.startedBy,
      total: data.total ?? job.total, nudges: data.nudges ?? job.nudges,
      done: data.done ?? job.done, ready: data.ready ?? job.ready,
      needsReview: data.needsReview ?? job.needsReview, failed: data.failed ?? job.failed,
      // The list intentionally omits per-nudge errors; preserve details already fetched.
      errors: Array.isArray(data.errors) ? data.errors : job.errors,
      error: data.error ?? null, createdAt: data.createdAt ?? job.createdAt,
      updatedAt: data.updatedAt ?? job.updatedAt, finishedAt: data.finishedAt ?? job.finishedAt,
      // the list read carries neither — keep what the detail read gave us
      nudgeStatuses: data.nudgeStatuses ?? job.nudgeStatuses,
      previewLinks: data.previewLinks ?? job.previewLinks,
      monitorError: '', pollingPaused: false,
      cancelState: data.status === 'running' ? job.cancelState : undefined,
      cancelError: data.status === 'running' ? job.cancelError : '',
    };
  }

  private validStatus(status: string): boolean {
    // 'canceling' is a live state the backend reports between Stop and the
    // final 'canceled'. Rejecting it here dropped the job from every list and
    // stopped its poll, so a stopping job simply vanished.
    return ['running', 'canceling', 'completed', 'error', 'canceled'].includes(status);
  }

  /** Still occupying the target's single job slot. */
  private isActive(status: string): boolean {
    return status === 'running' || status === 'canceling';
  }

  private poll(initial: NudgePortJob): void {
    let current = initial;
    // Timers outside Angular avoid a change-detection pass on every polling tick.
    this.zone.runOutsideAngular(() => {
      const subscription = timer(0, 10000).pipe(
        exhaustMap(() => this.http.get<ApiResponse<JobStatus>>('/nudge-builder/port/job', {
          params: { jobId: initial.jobId, target: initial.target },
          context: this.context(initial.region),
        }).pipe(
          timeout(30000),
          map(response => {
            const data = this.unwrap(response);
            if (!data || !this.validStatus(data.status)) {
              throw new Error('Unrecognized job status.');
            }
            // The job's original client and region remain fixed when the dashboard client changes.
            current = this.mergeStatus(this.state.value.find(job => job.key === initial.key) || current, data);
            return current;
          }),
          catchError(error => {
            const paused = error instanceof HttpErrorResponse && [401, 403, 404].includes(error.status);
            current = {
              ...(this.state.value.find(job => job.key === initial.key) || current), pollingPaused: paused,
              monitorError: paused
                ? 'Status checking is paused. Your job may still be running. Check your access and try again.'
                : 'Unable to refresh progress. Retrying automatically; your job may still be running.',
            };
            return of(current);
          }),
        )),
        takeWhile(job => this.isActive(job.status) && !job.pollingPaused, true),
        tap(job => this.zone.run(() => this.update(job))),
      ).subscribe({ complete: () => this.polls.delete(initial.key) });
      this.polls.set(initial.key, subscription);
    });
  }

  private update(job: NudgePortJob): void {
    this.state.next(this.state.value.map(item => item.key === job.key ? job : item));
  }

  private stop(key: string): void {
    this.polls.get(key)?.unsubscribe();
    this.polls.delete(key);
  }

  private context(region: string): HttpContext {
    return new HttpContext().set(FORCE_REGION, region);
  }

  private unwrap<T>(response: ApiResponse<T>): T {
    if (!response?.success) throw new Error(response?.message || response?.error || 'The request could not be completed.');
    return response.data;
  }
}
